/**
 * État de session côté client.
 *
 * - le **jeton d'accès** est conservé en mémoire uniquement (jamais persisté) ;
 * - le **jeton de rafraîchissement** est opaque (30 jours) et persisté dans `localStorage` ;
 * - l'utilisateur, le contexte de société active et les sociétés disponibles sont exposés via
 *   le hook `useAuth` ;
 * - toute purge (déconnexion ou expiration) vide l'état et le stockage.
 *
 * Le module branche le client HTTP (`setApiAuthHandlers`) afin que le rafraîchissement
 * automatique sur `401` mette à jour la session sans cycle d'import.
 */

import {
  type AuthContext,
  type AuthProfile,
  type AuthSessionResponse,
  type AuthTenantSummary,
  type AuthUser,
  type LoginInput,
  type PermissionCode,
  type RegisterInvitationInput,
  type RegisterCompanyInput,
  hasPermission,
} from '@noryvox/shared';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

import {
  getMeApi,
  loginApi,
  logoutApi,
  registerInvitationApi,
  registerCompanyApi,
  switchTenantApi,
  type LogoutApiPayload,
} from '../api/auth.api';
import { setApiAuthHandlers } from '../api/client';

/** État d'authentification exposé à l'interface. */
export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  context: AuthContext | null;
  availableTenants: AuthTenantSummary[];
}

/** Raison transmise à la page de connexion lors d'une redirection depuis une route protégée. */
export const SESSION_EXPIRED_REASON = 'session-expired';

const REFRESH_TOKEN_STORAGE_KEY = 'noryvox.refresh-token';

function readStoredRefreshToken(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function persistRefreshToken(token: string | null): void {
  try {
    if (token === null) {
      window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    } else {
      window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
    }
  } catch {
    // Stockage indisponible : la session reste valable en mémoire pour l'onglet courant.
  }
}

let accessToken: string | null = null;
let refreshToken: string | null = readStoredRefreshToken();

let state: AuthState = {
  status: refreshToken === null ? 'anonymous' : 'loading',
  user: null,
  context: null,
  availableTenants: [],
};

const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

function setState(next: AuthState): void {
  state = next;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): AuthState {
  return state;
}

/** Retourne le jeton d'accès courant (en mémoire). */
export function getAccessToken(): string | null {
  return accessToken;
}

/** Retourne le jeton de rafraîchissement courant (persisté). */
export function getRefreshToken(): string | null {
  return refreshToken;
}

/** Applique une session complète (connexion, inscription, rafraîchissement, changement de société). */
export function applySession(session: AuthSessionResponse): void {
  accessToken = session.accessToken;
  refreshToken = session.refreshToken;
  persistRefreshToken(session.refreshToken);
  const tenant = session.context.tenant;
  const availableTenants = state.availableTenants.some((entry) => entry.id === tenant.id)
    ? state.availableTenants
    : [tenant, ...state.availableTenants];
  setState({
    status: 'authenticated',
    user: session.user,
    context: session.context,
    availableTenants,
  });
}

/** Applique le profil complet (`GET /auth/me`) : utile pour les sociétés disponibles. */
export function setProfile(profile: AuthProfile): void {
  setState({
    status: 'authenticated',
    user: profile.user,
    context: profile.context,
    availableTenants: profile.availableTenants,
  });
}

/** Purge la session : jetons (mémoire et stockage) et état applicatif. */
export function clearSession(): void {
  accessToken = null;
  refreshToken = null;
  persistRefreshToken(null);
  setState({ status: 'anonymous', user: null, context: null, availableTenants: [] });
}

setApiAuthHandlers({
  getAccessToken,
  getRefreshToken,
  applySession,
  onSessionExpired: clearSession,
});

let bootstrapStarted = false;

/** Recharge la session au démarrage : `GET /auth/me` (rafraîchi automatiquement si besoin). */
async function bootstrapSession(): Promise<void> {
  if (bootstrapStarted) {
    return;
  }
  bootstrapStarted = true;

  if (getRefreshToken() === null) {
    clearSession();
    return;
  }

  try {
    const profile = await getMeApi();
    setProfile(profile);
  } catch {
    clearSession();
  }
}

async function login(payload: LoginInput): Promise<void> {
  const session = await loginApi(payload);
  applySession(session);
}

async function registerCompany(payload: RegisterCompanyInput): Promise<void> {
  const session = await registerCompanyApi(payload);
  applySession(session);
}

async function registerInvitation(payload: RegisterInvitationInput): Promise<void> {
  const session = await registerInvitationApi(payload);
  applySession(session);
}

async function logout(options: LogoutApiPayload = {}): Promise<void> {
  const token = getRefreshToken();
  try {
    await logoutApi({ refreshToken: token ?? undefined, allDevices: options.allDevices ?? false });
  } catch {
    // La purge locale prime : une API injoignable ne doit pas laisser une session ouverte.
  } finally {
    clearSession();
  }
}

async function switchTenant(tenantId: string): Promise<void> {
  const session = await switchTenantApi(tenantId);
  applySession(session);
}

/** Recharge le profil depuis l'API (ex. après activation ou désactivation du second facteur). */
async function refreshProfile(): Promise<void> {
  setProfile(await getMeApi());
}

/** Valeur du contexte d'authentification. */
export interface AuthContextValue extends AuthState {
  isAuthenticated: boolean;
  tenant: AuthTenantSummary | null;
  /** Codes de rôles effectifs de la société active. */
  roles: readonly string[];
  /** Codes de permissions effectives de la société active (déni par défaut). */
  permissions: readonly string[];
  /** Vrai si l'utilisateur courant dispose de la permission demandée. */
  can: (required: PermissionCode | readonly PermissionCode[], requireAll?: boolean) => boolean;
  bootstrap: () => Promise<void>;
  login: (payload: LoginInput) => Promise<void>;
  registerCompany: (payload: RegisterCompanyInput) => Promise<void>;
  registerInvitation: (payload: RegisterInvitationInput) => Promise<void>;
  logout: (options?: LogoutApiPayload) => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  /** Recharge le profil (`GET /auth/me`) sans reconstruire la session. */
  refreshProfile: () => Promise<void>;
}

/** Hook d'accès à la session et aux actions d'authentification. */
export function useAuth(): AuthContextValue {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const bootstrap = useCallback((): Promise<void> => bootstrapSession(), []);
  const loginCallback = useCallback((payload: LoginInput): Promise<void> => login(payload), []);
  const registerCompanyCallback = useCallback(
    (payload: RegisterCompanyInput): Promise<void> => registerCompany(payload),
    [],
  );
  const registerInvitationCallback = useCallback(
    (payload: RegisterInvitationInput): Promise<void> => registerInvitation(payload),
    [],
  );
  const logoutCallback = useCallback(
    (options?: LogoutApiPayload): Promise<void> => logout(options),
    [],
  );
  const switchTenantCallback = useCallback(
    (tenantId: string): Promise<void> => switchTenant(tenantId),
    [],
  );
  const refreshProfileCallback = useCallback((): Promise<void> => refreshProfile(), []);

  return useMemo<AuthContextValue>(
    () => ({
      ...snapshot,
      isAuthenticated: snapshot.status === 'authenticated',
      tenant: snapshot.context?.tenant ?? null,
      roles: snapshot.context?.roles ?? [],
      permissions: snapshot.context?.permissions ?? [],
      can: (required, requireAll = false) =>
        hasPermission(snapshot.context?.permissions ?? [], required, {
          isPlatformAdmin: snapshot.user?.isPlatformAdmin ?? false,
          requireAll,
        }),
      bootstrap,
      login: loginCallback,
      registerCompany: registerCompanyCallback,
      registerInvitation: registerInvitationCallback,
      logout: logoutCallback,
      switchTenant: switchTenantCallback,
      refreshProfile: refreshProfileCallback,
    }),
    [
      snapshot,
      bootstrap,
      loginCallback,
      registerCompanyCallback,
      registerInvitationCallback,
      logoutCallback,
      switchTenantCallback,
      refreshProfileCallback,
    ],
  );
}
